import { useDispatch } from 'react-redux'
import { setUser, initialState, calculatePermissions } from 'store/auth/userSlice'
import {
    apiAcceptEula,
    apiGetUserSession,
    apiGetEulaStatus,
    apiSignIn,
    apiSignOut,
    apiSignUp,
} from 'services/AuthService'
import { onSignInSuccess, onSignOutSuccess } from 'store/auth/sessionSlice'
import appConfig from 'configs/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'
import { getAccountData } from 'services/MediatorService'

function useAuth() {
    const dispatch = useDispatch()

    const navigate = useNavigate()

    const query = useQuery()

    const completeSignIn = ({ accessToken, user, userData, redirectUrl }) => {
        const permissions = calculatePermissions(userData)

        dispatch(onSignInSuccess(accessToken))
        if (user) {
            dispatch(
                setUser({
                    ...user,
                    ...userData,
                    permissions,
                } || null)
            )
        }

        navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath)
    }

    const signIn = async (values) => {
        try {
            const resp = await apiSignIn(values)
            if (resp.error) {
                return {
                    status: 'failed',
                    message: resp.error?.message || 'Network Error!',
                }
            } else if (resp.data && resp.data.session) {
                const {
                    session: { access_token },
                } = resp.data
                const userData = await getAccountData(resp.data.user.id)
                const redirectUrl = query.get(REDIRECT_URL_KEY)

                const eulaStatusResp = await apiGetEulaStatus(access_token)
                const hasReadEula = Boolean(eulaStatusResp?.is_EULA_read)

                if (!hasReadEula) {
                    return {
                        status: 'eula_required',
                        message: 'Please accept the EULA to continue.',
                        pendingAuth: {
                            accessToken: access_token,
                            user: resp.data.user,
                            userData,
                            redirectUrl,
                        },
                    }
                }

                completeSignIn({
                    accessToken: access_token,
                    user: resp.data.user,
                    userData,
                    redirectUrl,
                })
                return {
                    status: 'success',
                    message: '',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const acceptEulaAndCompleteSignIn = async (pendingAuth) => {
        try {
            if (!pendingAuth?.accessToken || !pendingAuth?.user) {
                return {
                    status: 'failed',
                    message: 'Missing sign-in state. Please sign in again.',
                }
            }

            await apiAcceptEula(pendingAuth.accessToken)
            completeSignIn(pendingAuth)
            return {
                status: 'success',
                message: '',
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.message || errors.toString(),
            }
        }
    }

    const signUp = async (values) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            resp.data.user || {
                                avatar: '',
                                userName: 'Anonymous',
                                authority: ['USER'],
                                email: '',
                            }
                        )
                    )
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const handleSignOut = () => {
        dispatch(onSignOutSuccess())
        dispatch(setUser(initialState))
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        await apiSignOut()
        handleSignOut()
    }

    const checkUserSession = async () => {
        const { data, error } = await apiGetUserSession()
        if (data?.session !== undefined && error === null) return true
        else return false
    }

    const getAuthId = async () => {
        try {
            const { data, error } = await apiGetUserSession()

            if (error) {
                throw error
            }
            if (
                data?.session !== undefined &&
                data?.session?.user?.id &&
                error === null
            ) {
                return data?.session?.user?.id
            }
            throw new Error('Unauthenticated User')
        } catch (err) {
            throw err
        }
    }

    return {
        authenticated: checkUserSession,
        signIn,
        acceptEulaAndCompleteSignIn,
        signUp,
        signOut,
        getAuthId,
    }
}

export default useAuth
